/**
 * Fixture schedule model and Firestore helpers.
 *
 * Knockout rows may have homeTeam/awayTeam = TBD with human slot labels; bracket resolution
 * writes real teams plus homeTeamSource / awayTeamSource (`seed` | `standings` | `result` | `admin`).
 * See docs/flows/bracket-fill-in.md and packages/domain/src/helpers/knockout/bracketSlots.ts.
 */
/* Legacy note: loadFixtures() also supports one-off JSON → Firestore import. */

import {
  Timestamp,
  collection,
  doc,
  writeBatch,
  type Firestore,
  type DocumentData,
  query,
  type QueryConstraint,
  type Query,
} from 'firebase/firestore';

/** Max writes per Firestore batch (limit 500; stay under). */
const FIXTURES_BATCH_SIZE = 450;
import { ACTIVE_TOURNAMENT } from '../constants';
import { type Country, countriesInfo, Country as CountryEnum } from './country';
import fixturesJson from './fixtures.json';

export type UkBroadcaster = 'BBC One' | 'BBC Two' | 'ITV1' | 'ITV4' | 'BBC One + ITV1';

export interface Fixture {
  date: Timestamp;
  location: string;
  homeTeam: Country;
  awayTeam: Country;
  stage: gameStage;
  /** Monotonic order within the tournament (sheet MatchNumber). */
  sortOrder?: number;
  /** Sheet match id, e.g. R32_7, SF_1. */
  matchId?: string;
  /** API-Football fixture id for live score sync. */
  apiFixtureId?: number;
  /** Tournament matchday from the workbook (column Matchday). */
  matchday?: number;
  /** e.g. "Round of 32 | 7", "Group A". */
  bracketLabel?: string;
  /** Knockout placeholder text when homeTeam is TBD (e.g. "Winner E"). */
  homeSlotLabel?: string;
  awaySlotLabel?: string;
  /** How homeTeam/awayTeam was populated (knockout bracket resolution). */
  homeTeamSource?: FixtureTeamSource;
  awayTeamSource?: FixtureTeamSource;
  venueName?: string;
  cityName?: string;
  /** Stable slug matching mapVenues.json id (e.g. "los-angeles"). */
  mapVenueId?: string;
  /** UK free-to-air TV channel(s) for this match; simulcasts use `BBC One + ITV1`. */
  ukBroadcaster?: UkBroadcaster;
}

export type FixtureTeamSource = 'seed' | 'standings' | 'result' | 'admin';

export enum gameStage {
  groupStage = 0,
  roundOf32 = 1,
  roundOf16 = 2,
  quarterFinal = 3,
  semiFinal = 4,
  thirdPlayoff = 5,
  final = 6,
}

/** Spreadsheet / FIFA 3-letter codes → internal `Country` enum (WC 2026). */
export const sheetCodeToCountry: Record<string, Country> = {
  ALG: CountryEnum.ALG,
  ARG: CountryEnum.AR,
  AUS: CountryEnum.AU,
  AUT: CountryEnum.AT,
  BEL: CountryEnum.BE,
  BRA: CountryEnum.BR,
  CAN: CountryEnum.CA,
  CPV: CountryEnum.CPV,
  COL: CountryEnum.CO,
  CRO: CountryEnum.HR,
  CUW: CountryEnum.CUW,
  ECU: CountryEnum.EC,
  EGY: CountryEnum.EGY,
  ENG: CountryEnum.GB_ENG,
  FRA: CountryEnum.FR,
  GER: CountryEnum.DE,
  GHA: CountryEnum.GH,
  HAI: CountryEnum.HT,
  IRN: CountryEnum.IR,
  CIV: CountryEnum.CIV,
  JPN: CountryEnum.JP,
  JOR: CountryEnum.JOR,
  MEX: CountryEnum.MX,
  MAR: CountryEnum.MA,
  NED: CountryEnum.NL,
  NZL: CountryEnum.NZ,
  NOR: CountryEnum.NO,
  PAN: CountryEnum.PA,
  PAR: CountryEnum.PAR,
  POR: CountryEnum.PT,
  QAT: CountryEnum.QA,
  KSA: CountryEnum.SA,
  SCO: CountryEnum.GB_SCT,
  SEN: CountryEnum.SN,
  RSA: CountryEnum.ZA,
  KOR: CountryEnum.KR,
  ESP: CountryEnum.ES,
  SUI: CountryEnum.CH,
  TUN: CountryEnum.TN,
  USA: CountryEnum.US,
  UZB: CountryEnum.UZB,
  URU: CountryEnum.UY,
  BIH: CountryEnum.BIH,
  SWE: CountryEnum.SE,
  TUR: CountryEnum.TR,
  CZE: CountryEnum.CZ,
  COD: CountryEnum.COD,
  IRQ: CountryEnum.IRQ,
};

function resolveTeamFromFixture(
  homeOrAway: 'home' | 'away',
  row: { HomeTeam: string; AwayTeam: string; HomeCode?: string; AwayCode?: string },
): Country {
  const code = homeOrAway === 'home' ? row.HomeCode : row.AwayCode;
  if (code && sheetCodeToCountry[code]) {
    return sheetCodeToCountry[code];
  }
  const name = homeOrAway === 'home' ? row.HomeTeam : row.AwayTeam;
  const byName = Object.values(countriesInfo).filter((x) => x.name === name);
  if (byName.length === 1) {
    return byName[0].id;
  }
  return CountryEnum.TBD;
}

export function getGameStage(roundNumber: number): gameStage {
  if (roundNumber <= 3) return gameStage.groupStage;
  if (roundNumber === 4) return gameStage.roundOf32;
  if (roundNumber === 5) return gameStage.roundOf16;
  if (roundNumber === 6) return gameStage.quarterFinal;
  if (roundNumber === 7) return gameStage.semiFinal;
  if (roundNumber === 8) return gameStage.thirdPlayoff;
  if (roundNumber === 9) return gameStage.final;
  return gameStage.groupStage;
}

export function queryFixtures(db: Firestore, ...queries: QueryConstraint[]): Query {
  return query(collection(db, 'competitions', ACTIVE_TOURNAMENT, 'fixtures'), ...queries);
}

export type FixtureJsonRow = {
  MatchNumber: number;
  MatchId?: string;
  RoundNumber: number;
  Matchday?: number;
  DateUtc: string;
  Location: string;
  HomeTeam: string;
  AwayTeam: string;
  HomeCode?: string;
  AwayCode?: string;
  Group?: string | null;
  HomeTeamScore?: null;
  AwayTeamScore?: null;
  BracketLabel?: string;
  VenueName?: string;
  CityName?: string;
  MapVenueId?: string;
  HomeSlotLabel?: string;
  AwaySlotLabel?: string;
  UkBroadcaster?: UkBroadcaster;
};

/**
 * Writes `fixtures.json` into `competitions/{ACTIVE_TOURNAMENT}/fixtures` using
 * stable ids `wc26-m{MatchNumber}` so re-runs overwrite the same matches (no duplicates).
 * Chunks commits to stay under Firestore's 500-op batch limit.
 */
export async function loadFixtures(db: Firestore): Promise<void> {
  const rows = fixturesJson as FixtureJsonRow[];
  let batch = writeBatch(db);
  let opsInBatch = 0;

  const commitIfNeeded = async (force: boolean) => {
    if (force && opsInBatch > 0) {
      await batch.commit();
      batch = writeBatch(db);
      opsInBatch = 0;
    }
  };

  for (const fixture of rows) {
    const docId = `wc26-m${fixture.MatchNumber}`;
    const fixtureRef = doc(db, 'competitions', ACTIVE_TOURNAMENT, 'fixtures', docId);
    const homeTeam = resolveTeamFromFixture('home', fixture);
    const awayTeam = resolveTeamFromFixture('away', fixture);

    const date = Timestamp.fromDate(new Date(Date.parse(fixture.DateUtc.replace(' ', 'T'))));

    const document: DocumentData = {
      date: date,
      location: fixture.Location,
      homeTeam: homeTeam,
      awayTeam: awayTeam,
      stage: getGameStage(fixture.RoundNumber),
      sortOrder: fixture.MatchNumber,
    };

    if (fixture.MatchId) document.matchId = fixture.MatchId;
    if (fixture.Matchday != null) document.matchday = fixture.Matchday;
    if (fixture.BracketLabel) document.bracketLabel = fixture.BracketLabel;
    if (fixture.VenueName) document.venueName = fixture.VenueName;
    if (fixture.CityName) document.cityName = fixture.CityName;
    if (fixture.MapVenueId) document.mapVenueId = fixture.MapVenueId;
    if (fixture.HomeSlotLabel) document.homeSlotLabel = fixture.HomeSlotLabel;
    if (fixture.AwaySlotLabel) document.awaySlotLabel = fixture.AwaySlotLabel;
    if (fixture.UkBroadcaster) document.ukBroadcaster = fixture.UkBroadcaster;

    batch.set(fixtureRef, document, { merge: true });
    opsInBatch++;
    if (opsInBatch >= FIXTURES_BATCH_SIZE) {
      await commitIfNeeded(true);
    }
  }

  await commitIfNeeded(true);
  console.log(`loadFixtures: wrote ${rows.length} fixtures to competitions/${ACTIVE_TOURNAMENT}/fixtures`);
}

/** Client-side fixture list from bundled JSON (public Teams page, offline previews). */
export function getFixturesFromJson(): Fixture[] {
  const rows = fixturesJson as FixtureJsonRow[];
  return rows
    .map((row) => {
      const homeTeam = resolveTeamFromFixture('home', row);
      const awayTeam = resolveTeamFromFixture('away', row);
      const date = Timestamp.fromDate(new Date(Date.parse(row.DateUtc.replace(' ', 'T'))));
      const fixture: Fixture = {
        date,
        location: row.Location,
        homeTeam,
        awayTeam,
        stage: getGameStage(row.RoundNumber),
        sortOrder: row.MatchNumber,
      };
      if (row.MatchId) fixture.matchId = row.MatchId;
      if (row.Matchday != null) fixture.matchday = row.Matchday;
      if (row.BracketLabel) fixture.bracketLabel = row.BracketLabel;
      if (row.VenueName) fixture.venueName = row.VenueName;
      if (row.CityName) fixture.cityName = row.CityName;
      if (row.MapVenueId) fixture.mapVenueId = row.MapVenueId;
      if (row.HomeSlotLabel) fixture.homeSlotLabel = row.HomeSlotLabel;
      if (row.AwaySlotLabel) fixture.awaySlotLabel = row.AwaySlotLabel;
      if (row.UkBroadcaster) fixture.ukBroadcaster = row.UkBroadcaster;
      return fixture;
    })
    .sort((a, b) => {
      const d = a.date.toMillis() - b.date.toMillis();
      if (d !== 0) return d;
      return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
    });
}
